import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  BarChart3,
  ShoppingCart,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
} from "lucide-react";
import { useAuth } from "../../context/Auth";
import ExportData from "../../components/common/ExportData";
import ReturnModal from "../../components/modals/ReturnModal";

const BranchSalesDetails = () => {
  const { requestWithAuth } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [tableData, setTableData] = useState([]);

  const getTodayDate = () => {
    const today = new Date();
    // adjust timezone offset if needed or use local string
    const offset = today.getTimezoneOffset()
    const adjustedDate = new Date(today.getTime() - (offset*60*1000))
    return adjustedDate.toISOString().split('T')[0]
  };

  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [branches, setBranches] = useState([]);

  // New states for filters and checkboxes
  const [sessionFilter, setSessionFilter] = useState("");
  const [recordedByFilter, setRecordedByFilter] = useState("");
  const [dispensers, setDispensers] = useState([]);
  const [selectedSales, setSelectedSales] = useState([]);
  const [showReturnModal, setShowReturnModal] = useState(false);

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?paginated=false&status=Active`,
        "GET"
      );
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches.");
    }
  };

  const fetchDispensers = async () => {
    try {
      const data = await requestWithAuth(
        `/users/accounts/?paginated=false&status=Active&staff_role=dispenser`,
        "GET"
      );
      setDispensers(data?.results || data || []);
    } catch (error) {
      console.error("Error fetching dispensers:", error);
    }
  };

  const fetchSales = async (page_size, search_query, current_page, session_val, recorded_by_val, branch_id, date_val) => {
    if (!branch_id || !date_val) {
      setSales([]);
      setTotalPages(0);
      return;
    }
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/report/sales/today/${branch_id}?page=${current_page}&pageSize=${page_size}&search=${search_query}&session=${session_val}&recorded_by=${recorded_by_val}&date=${date_val}`,
        "GET"
      );
      setSales(data?.results || []);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / page_size));
    } catch (error) {
      console.error("Error fetching sales details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async (session_val, recorded_by_val, branch_id, date_val) => {
    if (!branch_id || !date_val) {
      setTableData([]);
      return;
    }
    try {
      const data = await requestWithAuth(
        `/report/sales/today/${branch_id}?paginated=false&session=${session_val}&recorded_by=${recorded_by_val}&date=${date_val}`,
        "GET"
      );
      setTableData(data || []);
    } catch (error) {
      console.error("Error fetching export data:", error);
    }
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedSales = useMemo(() => {
    let sortableItems = [...sales];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle numeric values appropriately
        if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [sales, sortConfig]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchBranches();
    fetchDispensers();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchSales(pageSize, search, currentPage, sessionFilter, recordedByFilter, selectedBranch, selectedDate);
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [search, currentPage, pageSize, sessionFilter, recordedByFilter, selectedBranch, selectedDate]);

  useEffect(() => {
    fetchTableData(sessionFilter, recordedByFilter, selectedBranch, selectedDate);
  }, [sessionFilter, recordedByFilter, selectedBranch, selectedDate]);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[32px] h-[32px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-600 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const exportData = useMemo(() => {
    return tableData.map((item) => ({
      "Sale ID": `#${item.id}`,
      "Drug Name": item.drug,
      "Quantity": Number(item.quantity || 0).toFixed(2),
      "Cost Sales": Number(item.total_cost_price || 0).toFixed(2),
      "Selling Sales": Number(item.total_selling_price || 0).toFixed(2),
      "Profit Margin": Number(item.profit || 0).toFixed(2),
      "Branch": item?.branch,
      "Recorded By": item.recorded_by,
      "Session": item.session,
      "Date": formatDate(item.created_at || item.date),
    }));
  }, [tableData]);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} className="text-gray-400 ml-1" />;
    }
    return sortConfig.direction === 'asc' ?
      <ArrowUp size={14} className="text-blue-600 ml-1" /> :
      <ArrowDown size={14} className="text-blue-600 ml-1" />;
  };

  const renderSortableHeader = (label, key, align = "left") => (
    <th
      className={`text-${align} py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none`}
      onClick={() => handleSort(key)}
    >
      <div className={`flex items-center ${align === "right" ? "justify-end" : "justify-start"}`}>
        {label}
        <SortIcon columnKey={key} />
      </div>
    </th>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1700px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <span>POS</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Branch Sales</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Branch Sales Details</h1>
              <p className="text-gray-600 text-sm">
                Detailed view of transactions for a specific branch and date
              </p>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1 max-w-sm">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Branch <span className="text-red-500">*</span></label>
                    <select
                        value={selectedBranch}
                        onChange={(e) => {
                            setSelectedBranch(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                        <option value="">Select Branch</option>
                        {branches.map((branch) => (
                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                        ))}
                    </select>
                </div>
                <div className="flex-1 max-w-sm">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date <span className="text-red-500">*</span></label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => {
                            setSelectedDate(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                </div>
            </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Controls */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search details..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex-1 max-w-[200px]">
                <select
                  value={sessionFilter}
                  onChange={(e) => {
                    setSessionFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Sessions</option>
                  <option value="morning">Morning</option>
                  <option value="evening">Evening</option>
                </select>
              </div>

              <div className="flex-1 max-w-[200px]">
                <select
                  value={recordedByFilter}
                  onChange={(e) => {
                    setRecordedByFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Dispensers</option>
                  {dispensers.map((dispenser) => (
                    <option key={dispenser.id} value={dispenser.id}>
                      {dispenser.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Page Size & Actions */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {selectedSales.length > 0 && (
                  <button
                    onClick={() => setShowReturnModal(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Return Selected ({selectedSales.length})
                  </button>
                )}

                {/* Export Button */}
                <div className="flex">
                  <ExportData tableData={exportData} fileName={`branch-sales-${selectedBranch || 'all'}-${selectedDate}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {!selectedBranch || !selectedDate ? (
                <div className="flex items-center justify-center py-20 text-gray-500">
                    Please select a branch and date to view sales.
                </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span>Loading details...</span>
                </div>
              </div>
            ) : sales?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="py-4 px-6 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={sortedSales?.length > 0 && selectedSales.length === sortedSales.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSales(sortedSales);
                          } else {
                            setSelectedSales([]);
                          }
                        }}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    {renderSortableHeader("Sale ID", "id")}
                    {renderSortableHeader("Drug Name", "drug")}
                    {renderSortableHeader("Quantity", "quantity")}
                    {renderSortableHeader("Cost Sales", "total_cost_price")}
                    {renderSortableHeader("Selling Sales", "total_selling_price")}
                    {renderSortableHeader("Profit Margin", "profit")}
                    {renderSortableHeader("Recorded By", "recorded_by")}
                    {renderSortableHeader("Session", "session")}
                    {renderSortableHeader("Date", "created_at")}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedSales.map((sale, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-center">
                        <input
                          type="checkbox"
                          checked={selectedSales.some(item => item.id === sale.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSales([...selectedSales, sale]);
                            } else {
                              setSelectedSales(selectedSales.filter(item => item.id !== sale.id));
                            }
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        #{sale?.id}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {sale?.drug}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {Number(sale?.quantity || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {Number(sale?.total_cost_price || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {Number(sale?.total_selling_price || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-sm text-green-600 font-medium">
                        {Number(sale?.profit || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {sale?.recorded_by}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 capitalize">
                        {sale?.session}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 flex items-center gap-2">
                        <Calendar size={14} className="text-gray-400" />
                        {formatDate(sale?.created_at || sale?.date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-3">
                  <ShoppingCart className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No Details Found</h3>
                <p className="text-gray-500 text-sm mt-1">
                  We couldn't find any transaction details for this branch on the selected date.
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {sales?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page <span className="font-medium">{currentPage}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showReturnModal && (
        <ReturnModal
          closeReturnModal={() => {
            setShowReturnModal(false);
            setSelectedSales([]);
          }}
          selectedItem={selectedSales}
          fetchSaleDetail={() => fetchSales(pageSize, search, currentPage, sessionFilter, recordedByFilter, selectedBranch, selectedDate)}
          isBulk={true}
        />
      )}
    </div>
  );
};

export default BranchSalesDetails;
